import { createContext, memo, type ReactNode, useContext, useEffect, useMemo } from 'react';

import { type Account as TAccount } from '@air/api/types';
import { type FullWorkspace, type Workspace } from '@air/types';

import { useInitializeAnalyticsTools } from '~/hooks/useInitializeAnalyticsTools';
import { useAccount, usePublicAccount } from '~/swr-hooks/account/useAccount';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';

type AccountProviderProps = {
  children: ReactNode;
  workspace?: Pick<Workspace, 'id' | 'name'> & Partial<Pick<FullWorkspace, 'plan'>>;
};

export type AccountProviderContextValue = {
  data: TAccount | undefined;
  error?: unknown;
};

const defaultValue: AccountProviderContextValue = {
  data: undefined,
};

const AccountProviderContext = createContext<AccountProviderContextValue>(defaultValue);

export const AccountProvider = memo(({ children, workspace }: AccountProviderProps) => {
  const { data: account, error } = useAccount();
  const { initializeAnalyticsTools } = useInitializeAnalyticsTools();

  const value = useMemo(() => ({ data: account, error }), [account, error]);

  useEffect(() => {
    if (account) {
      initializeAnalyticsTools({ account, workspace });
    }
  }, [account, initializeAnalyticsTools, workspace]);

  return <AccountProviderContext.Provider value={value}>{children}</AccountProviderContext.Provider>;
});

AccountProvider.displayName = 'AccountProvider';

export const PublicAccountProvider = memo(({ children, workspace }: AccountProviderProps) => {
  const { data: account, error } = usePublicAccount();
  const { initializeAnalyticsTools, initializeAnonymousTools } = useInitializeAnalyticsTools();

  /**
   * usePublicAccount may return:
   * - account: when user is logged in
   * - null: when user is not logged in
   * - undefined: when there was an error during getting current account
   * But all our components expects account to of type Account | undefined
   * to simplify code, if it is null, we will convert it to undefined
   */
  const value = useMemo(() => ({ data: account ?? undefined, error }), [account, error]);

  useEffect(() => {
    if (account) {
      initializeAnalyticsTools({ account, workspace });
    } else if (workspace) {
      initializeAnonymousTools({ workspace });
    }
  }, [account, initializeAnalyticsTools, workspace, initializeAnonymousTools]);

  return <AccountProviderContext.Provider value={value}>{children}</AccountProviderContext.Provider>;
});

PublicAccountProvider.displayName = 'PublicAccountProvider';

export const useAccountContext = () => {
  const context = useContext(AccountProviderContext);

  if (context === defaultValue) {
    const error = 'AccountProviderContext used outside of AccountProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
